import React, { useEffect, useState } from "react";
import api from "../../../api";
import { IMAGE_URL } from "../../../path";

interface Retailer {
    id: number;
    name: string;
    logo: string | null;
    link: string;
    active: boolean;
    display_order: number;
}

interface RetailerModalProps {
    retailer: Retailer | null;
    onClose: () => void;
    onSaved: () => void;
}

const RetailerModal = ({
                           retailer,
                           onClose,
                           onSaved,
                       }: RetailerModalProps) => {
    const [name, setName] = useState("");
    const [link, setLink] = useState("");

    const [logo, setLogo] = useState<File | null>(null);
    const [logoPreview, setLogoPreview] =
        useState<string | null>(null);

    const [active, setActive] = useState(true);
    const [displayOrder, setDisplayOrder] = useState(0);

    const [loading, setLoading] = useState(false);

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const isEditing = !!retailer;

    useEffect(() => {
        if (retailer) {
            setName(retailer.name || "");
            setLink(retailer.link || "");
            setActive(retailer.active);
            setDisplayOrder(
                retailer.display_order ?? 0
            );

            setLogo(null);

            if (retailer.logo) {
                setLogoPreview(
                    `${IMAGE_URL}/${retailer.logo}`
                );
            } else {
                setLogoPreview(null);
            }
        } else {
            setName("");
            setLink("");
            setLogo(null);
            setLogoPreview(null);
            setActive(true);
            setDisplayOrder(0);
        }

        setError("");
        setSuccess("");
    }, [retailer]);

    const handleLogoChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        setLogo(file);

        const objectUrl = URL.createObjectURL(file);
        setLogoPreview(objectUrl);
    };

    const handleSave = async () => {
        setError("");
        setSuccess("");

        if (!name.trim()) {
            setError("Please enter a retailer name.");
            return;
        }

        if (!isEditing && !logo) {
            setError("Please select a retailer logo.");
            return;
        }

        if (!link.trim()) {
            setError("Please enter the retailer product page link.");
            return;
        }

        setLoading(true);

        try {
            const formData = new FormData();

            formData.append("name", name.trim());
            formData.append("link", link.trim());
            formData.append(
                "active",
                active ? "1" : "0"
            );
            formData.append(
                "display_order",
                String(Number(displayOrder) || 0)
            );

            if (logo) {
                formData.append("logo", logo);
            }

            if (isEditing) {
                formData.append("_method", "PUT");

                await api.post(
                    `/admin/retailers/${retailer.id}`,
                    formData,
                    {
                        headers: {
                            "Content-Type":
                                "multipart/form-data",
                        },
                    }
                );

                setSuccess(
                    "Retailer updated successfully."
                );
            } else {
                await api.post(
                    "/admin/retailers",
                    formData,
                    {
                        headers: {
                            "Content-Type":
                                "multipart/form-data",
                        },
                    }
                );

                setSuccess(
                    "Retailer created successfully."
                );
            }

            await onSaved();
        } catch (err: any) {
            console.error(
                "Failed to save retailer:",
                err
            );

            if (err.response?.data?.errors) {
                const validationErrors =
                    Object.values(
                        err.response.data.errors
                    ).flat();

                setError(
                    validationErrors.length
                        ? String(validationErrors[0])
                        : "Please check the form and try again."
                );
            } else {
                setError(
                    err.response?.data?.message ||
                    "Failed to save retailer."
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="admin-modal-overlay"
            onClick={onClose}
        >
            <div
                className="admin-modal"
                onClick={(event) =>
                    event.stopPropagation()
                }
            >
                <div className="admin-modal-header">
                    <h2>
                        {isEditing
                            ? "Edit Retailer"
                            : "Add Retailer"}
                    </h2>

                    <button
                        type="button"
                        className="admin-modal-close"
                        onClick={onClose}
                        disabled={loading}
                    >
                        ×
                    </button>
                </div>

                <div className="admin-modal-content">
                    {error && (
                        <div className="form-error">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="form-success">
                            {success}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="retailer-name">
                            Retailer Name
                        </label>

                        <input
                            id="retailer-name"
                            type="text"
                            value={name}
                            onChange={(event) =>
                                setName(event.target.value)
                            }
                            placeholder="Retailer name"
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="retailer-logo">
                            Logo
                        </label>

                        <input
                            id="retailer-logo"
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                            onChange={handleLogoChange}
                            disabled={loading}
                        />

                        {logoPreview && (
                            <div className="admin-image-preview">
                                <img
                                    src={logoPreview}
                                    alt={`${name || "Retailer"} logo`}
                                />
                            </div>
                        )}
                    </div>

                    <div className="form-group">
                        <label htmlFor="retailer-link">
                            Product Page Link
                        </label>

                        <input
                            id="retailer-link"
                            type="url"
                            value={link}
                            onChange={(event) =>
                                setLink(event.target.value)
                            }
                            placeholder="https://www.example.com/product"
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="retailer-display-order">
                            Display Order
                        </label>

                        <input
                            id="retailer-display-order"
                            type="number"
                            min="0"
                            value={displayOrder}
                            onChange={(event) =>
                                setDisplayOrder(
                                    Number(event.target.value)
                                )
                            }
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label className="admin-checkbox-label">
                            <input
                                type="checkbox"
                                checked={active}
                                onChange={(event) =>
                                    setActive(
                                        event.target.checked
                                    )
                                }
                                disabled={loading}
                            />

                            <span>
                                Active
                            </span>
                        </label>
                    </div>
                </div>

                <div className="admin-modal-actions">
                    <button
                        type="button"
                        className="admin-table-button"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        className="admin-primary-button"
                        onClick={handleSave}
                        disabled={loading}
                    >
                        {loading
                            ? "Saving..."
                            : isEditing
                                ? "Save Changes"
                                : "Add Retailer"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RetailerModal;
